import React, { useState, useEffect, useContext } from 'react';
import { AuthContext } from './AuthContext';
import { useMultiHOA } from './MultiHOAContext';
import api from './api';
import CommunityInfoPanel from './CommunityInfoPanel';
import SchedulingChannels from './SchedulingChannels';
import { PlusIcon, CheckIcon, XMarkIcon, ClockIcon, Cog6ToothIcon } from '@heroicons/react/24/outline';

const SchedulerSystem = () => {
  const { user } = useContext(AuthContext);
  const { activeHoaId } = useMultiHOA();
  const [channels, setChannels] = useState([]);
  const [activeChannel, setActiveChannel] = useState(null);
  const [requests, setRequests] = useState([]);
  const [creating, setCreating] = useState(false);
  const [loading, setLoading] = useState(true);
  const [showAvailability, setShowAvailability] = useState(false);
  const [showCommunityInfo, setShowCommunityInfo] = useState(false);
  const [form, setForm] = useState({
    title: '',
    purpose: '',
    modality: 'zoom',
    location: '',
    link: '',
    slots: []
  });
  const [preview, setPreview] = useState(null);
  const [isPreviewing, setIsPreviewing] = useState(false);
  const [showMembersAvailability, setShowMembersAvailability] = useState(false);
  const [membersAvailability, setMembersAvailability] = useState(null);
  const [timePreference, setTimePreference] = useState('during_work');

  useEffect(() => {
    if (activeHoaId && user?.id) {
      // Provision first, then load
      provisionChannels();
    }
  }, [activeHoaId, user?.id]);

  useEffect(() => {
    if (activeChannel) {
      loadRequests();
    }
  }, [activeChannel]);

  // Preview availability when slots change
  useEffect(() => {
    const validSlots = (form.slots || []).filter(s => s.start_iso && s.end_iso);
    if (!activeChannel || validSlots.length === 0) {
      setPreview(null);
      return;
    }

    const timer = setTimeout(async () => {
      setIsPreviewing(true);
      try {
        const response = await api.post(
          `/api/scheduling/channels/${activeChannel.id}/availability/preview`,
          { slots: validSlots }
        );
        setPreview(response.data);
      } catch (error) {
        console.error('Preview failed:', error);
        setPreview(null);
      } finally {
        setIsPreviewing(false);
      }
    }, 350); // Debounce

    return () => clearTimeout(timer);
  }, [activeChannel, form.slots]);

  const provisionChannels = async () => {
    try {
      const response = await api.post('/api/scheduling/provision', {}, {
        params: { hoa_id: activeHoaId }
      });
      console.log('✅ Channels provisioned:', response.data);
      // Reload channels after provisioning
      await loadChannels();
    } catch (error) {
      console.error('❌ Error provisioning channels:', error);
    }
  };

  const loadChannels = async () => {
    try {
      setLoading(true);
      const { data } = await api.get('/api/scheduling/channels', {
        params: { hoa_id: activeHoaId }
      });
      setChannels(data.channels || []);
      
      // Auto-select first channel
      if (data.channels && data.channels.length > 0 && !activeChannel) {
        setActiveChannel(data.channels[0]);
      }
    } catch (error) {
      console.error('Error loading channels:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadRequests = async () => {
    try {
      const { data } = await api.get(`/api/scheduling/channels/${activeChannel.id}/requests`);
      setRequests(data.requests || []);
    } catch (error) {
      console.error('Error loading requests:', error);
    }
  };

  const loadMembersAvailability = async () => {
    if (!activeChannel) return;
    
    try {
      const response = await api.get(
        `/api/scheduling/channels/${activeChannel.id}/members/availability`
      );
      setMembersAvailability(response.data);
      setShowMembersAvailability(true);
    } catch (error) {
      console.error('Failed to load members availability:', error);
      alert('Could not load member availability');
    }
  };

  const addSuggestedSlots = async () => {
    if (!activeChannel) return;
    
    try {
      const response = await api.get(
        `/api/scheduling/channels/${activeChannel.id}/availability/suggest`,
        {
          params: {
            time_preference: timePreference
          }
        }
      );
      const suggestions = response.data.suggestions || [];
      
      // Convert ISO timestamps to datetime-local format (YYYY-MM-DDTHH:MM)
      const newSlots = suggestions.map(s => {
        const startDate = new Date(s.start_iso);
        const endDate = new Date(s.end_iso);
        
        // Format for datetime-local input: YYYY-MM-DDTHH:MM
        const formatForInput = (date) => {
          const year = date.getFullYear();
          const month = String(date.getMonth() + 1).padStart(2, '0');
          const day = String(date.getDate()).padStart(2, '0');
          const hours = String(date.getHours()).padStart(2, '0');
          const minutes = String(date.getMinutes()).padStart(2, '0');
          return `${year}-${month}-${day}T${hours}:${minutes}`;
        };
        
        return {
          start_iso: formatForInput(startDate),
          end_iso: formatForInput(endDate)
        };
      });
      
      setForm(f => ({
        ...f,
        slots: [...(f.slots || []), ...newSlots]
      }));
    } catch (error) {
      console.error('Failed to get suggestions:', error);
      alert('Could not get availability suggestions');
    }
  };

  const createRequest = async (e) => {
    e.preventDefault();
    
    if (!form.title || form.slots.length === 0) {
      alert('Please provide a title and at least one time slot');
      return;
    }

    try {
      await api.post(`/api/scheduling/channels/${activeChannel.id}/requests`, form);
      setCreating(false);
      setForm({
        title: '',
        purpose: '',
        modality: 'zoom',
        location: '',
        link: '',
        slots: []
      });
      await loadRequests();
    } catch (error) {
      console.error('Error creating request:', error);
      alert('Failed to create request. Please try again.');
    }
  };

  const addSlot = () => {
    setForm({
      ...form,
      slots: [...form.slots, { start_iso: '', end_iso: '' }]
    });
  };

  const removeSlot = (index) => {
    const newSlots = [...form.slots];
    newSlots.splice(index, 1);
    setForm({ ...form, slots: newSlots });
  };

  const updateSlot = (index, field, value) => {
    const newSlots = [...form.slots];
    newSlots[index] = { ...newSlots[index], [field]: value };
    setForm({ ...form, slots: newSlots });
  };

  return (
    <div className="min-h-[100dvh] flex flex-col lg:flex-row bg-gray-50">
      <SchedulingChannels
        channels={channels}
        activeChannelId={activeChannel?.id}
        onSelect={setActiveChannel}
        loading={loading}
      />

      <main className="flex-1 overflow-y-auto">
        <div className="max-w-5xl mx-auto p-3 sm:p-4 lg:p-6">
          {/* Header */}
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 mb-4 sm:mb-6">
            <div className="flex-1 min-w-0">
              <h1 className="text-xl sm:text-2xl font-bold text-gray-900 truncate">
                {activeChannel ? activeChannel.name : 'Scheduling'}
              </h1>
              <p className="text-xs sm:text-sm text-gray-600 mt-1">
                Propose and vote on meeting times
              </p>
            </div>

            {/* Mobile Community Info Button */}
            <button
              onClick={() => setShowCommunityInfo(true)}
              className="lg:hidden flex items-center gap-2 px-3 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors text-sm font-medium text-gray-700"
            >
              <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
              </svg>
              Community Info
            </button>

            <div className="flex gap-2 w-full sm:w-auto">
              <button
                className="flex-1 sm:flex-none px-3 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors flex items-center justify-center gap-2 text-sm"
                onClick={() => setShowAvailability(true)}
              >
                <Cog6ToothIcon className="h-4 w-4" />
                <span className="hidden sm:inline">My Availability</span>
                <span className="sm:hidden">Availability</span>
              </button>
              <button
                className="flex-1 sm:flex-none px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed text-sm"
                onClick={() => setCreating(true)}
                disabled={!activeChannel || creating}
              >
                <PlusIcon className="h-4 w-4" />
                <span>New</span>
              </button>
            </div>
          </div>

          {/* Create Request Form */}
          {creating && (
            <form onSubmit={createRequest} className="mb-4 sm:mb-6 bg-white p-4 sm:p-6 rounded-xl border shadow-sm space-y-3 sm:space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Meeting Title *
                </label>
                <input
                  type="text"
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="e.g., Monthly Board Meeting"
                  value={form.title}
                  onChange={(e) => setForm({ ...form, title: e.target.value })}
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Purpose
                </label>
                <textarea
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="What will be discussed?"
                  rows="2"
                  value={form.purpose}
                  onChange={(e) => setForm({ ...form, purpose: e.target.value })}
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Meeting Type
                  </label>
                  <select
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    value={form.modality}
                    onChange={(e) => setForm({ ...form, modality: e.target.value })}
                  >
                    <option value="zoom">Zoom</option>
                    <option value="in_person">In-Person</option>
                    <option value="phone">Phone</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Location (if in-person)
                  </label>
                  <input
                    type="text"
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Address or room"
                    value={form.location}
                    onChange={(e) => setForm({ ...form, location: e.target.value })}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Link (Zoom/Phone)
                  </label>
                  <input
                    type="text"
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Meeting URL or number"
                    value={form.link}
                    onChange={(e) => setForm({ ...form, link: e.target.value })}
                  />
                </div>
              </div>

              {/* Scheduling Preferences */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 space-y-3">
                <div className="flex items-center justify-between">
                  <h4 className="text-sm font-semibold text-gray-900">Scheduling Preferences</h4>
                  <button
                    type="button"
                    className="text-xs px-3 py-1.5 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors font-medium text-gray-700"
                    onClick={loadMembersAvailability}
                  >
                    👥 View Members' Time Restrictions
                  </button>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">
                      Time Preference for Suggestions
                    </label>
                    <select
                      value={timePreference}
                      onChange={(e) => setTimePreference(e.target.value)}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white"
                    >
                      <option value="during_work">During Work Hours (9 AM - 5 PM)</option>
                      <option value="after_work">After Work (4:30 PM - 8 PM)</option>
                      <option value="morning">Morning (7 AM - 9 AM)</option>
                      <option value="evening">Evening (6 PM - 9 PM)</option>
                    </select>
                  </div>
                  <div className="flex items-end">
                    <button
                      type="button"
                      className="w-full px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors font-medium text-sm"
                      onClick={addSuggestedSlots}
                    >
                      ✨ Suggest Top 3 Times
                    </button>
                  </div>
                </div>
              </div>

              {/* Time Slots */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="block text-sm font-medium text-gray-700">
                    Proposed Time Slots *
                  </label>
                </div>
                <div className="space-y-3">
                  {form.slots.map((slot, idx) => {
                    const pv = preview?.slots?.find(x => x.index === idx);
                    const isBest = preview && preview.ranked?.[0] === idx;
                    
                    return (
                      <div key={idx} className="border border-gray-200 rounded-lg p-3 bg-gray-50">
                        <div className="flex gap-2 items-center mb-2">
                          <input
                            type="datetime-local"
                            className="flex-1 border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white"
                            value={slot.start_iso}
                            onChange={(e) => updateSlot(idx, 'start_iso', e.target.value)}
                            required
                          />
                          <span className="text-gray-500">to</span>
                          <input
                            type="datetime-local"
                            className="flex-1 border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white"
                            value={slot.end_iso}
                            onChange={(e) => updateSlot(idx, 'end_iso', e.target.value)}
                            required
                          />
                          <button
                            type="button"
                            className="p-2 text-red-600 hover:bg-red-50 rounded-lg"
                            onClick={() => removeSlot(idx)}
                          >
                            <XMarkIcon className="h-5 w-5" />
                          </button>
                        </div>
                        
                        {/* Availability Preview */}
                        {isPreviewing && (
                          <div className="text-xs text-gray-500 flex items-center gap-2 mt-2">
                            <ClockIcon className="h-4 w-4 animate-spin" />
                            <span>Checking availability...</span>
                          </div>
                        )}
                        
                        {pv && !isPreviewing && (
                          <div className="text-xs mt-2 space-y-1.5">
                            <div className="flex items-center gap-3 flex-wrap">
                              <span className="inline-flex items-center gap-1 px-2 py-1 rounded bg-green-100 text-green-700 font-medium">
                                ✅ Available: {pv.counts.yes}
                              </span>
                              <span className="inline-flex items-center gap-1 px-2 py-1 rounded bg-yellow-100 text-yellow-700 font-medium">
                                ⚠️ If Needed: {pv.counts.if_needed}
                              </span>
                              <span className="inline-flex items-center gap-1 px-2 py-1 rounded bg-red-100 text-red-700 font-medium">
                                ⛔ Not Available: {pv.counts.no}
                              </span>
                              {isBest && (
                                <span className="ml-auto px-2 py-1 rounded bg-blue-600 text-white font-semibold text-xs">
                                  🌟 Recommended
                                </span>
                              )}
                            </div>
                            
                            {(pv.detail.no.length > 0 || pv.detail.if_needed.length > 0) && (
                              <details className="mt-2">
                                <summary className="cursor-pointer text-gray-600 hover:text-gray-800 font-medium">
                                  👥 Who can't make it? ({pv.detail.no.length + pv.detail.if_needed.length} members)
                                </summary>
                                <div className="mt-2 pl-4 space-y-1">
                                  {pv.detail.no.length > 0 && (
                                    <div>
                                      <div className="text-red-700 font-medium">Not Available:</div>
                                      <div className="flex flex-wrap gap-1 mt-1">
                                        {pv.detail.no.map((u, i) => (
                                          <span key={i} className="inline-block px-2 py-0.5 rounded bg-red-50 border border-red-200 text-red-700">
                                            {u.name}
                                          </span>
                                        ))}
                                      </div>
                                    </div>
                                  )}
                                  {pv.detail.if_needed.length > 0 && (
                                    <div className="mt-2">
                                      <div className="text-yellow-700 font-medium">If Needed:</div>
                                      <div className="flex flex-wrap gap-1 mt-1">
                                        {pv.detail.if_needed.map((u, i) => (
                                          <span key={i} className="inline-block px-2 py-0.5 rounded bg-yellow-50 border border-yellow-200 text-yellow-700">
                                            {u.name}
                                          </span>
                                        ))}
                                      </div>
                                    </div>
                                  )}
                                </div>
                              </details>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
                <button
                  type="button"
                  className="mt-2 text-sm text-blue-600 hover:text-blue-700 font-medium"
                  onClick={addSlot}
                >
                  + Add Time Slot
                </button>
              </div>

              <div className="flex gap-3 pt-4">
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  Create & Notify Members
                </button>
                <button
                  type="button"
                  className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                  onClick={() => {
                    setCreating(false);
                    setForm({
                      title: '',
                      purpose: '',
                      modality: 'zoom',
                      location: '',
                      link: '',
                      slots: []
                    });
                  }}
                >
                  Cancel
                </button>
              </div>
            </form>
          )}

          {/* Requests List */}
          <div className="space-y-4">
            {requests.length === 0 && !creating && (
              <div className="text-center py-12 bg-white rounded-xl border">
                <ClockIcon className="h-12 w-12 text-gray-400 mx-auto mb-3" />
                <p className="text-gray-600">No scheduling requests yet</p>
                <p className="text-sm text-gray-500 mt-1">
                  Create a request to propose meeting times
                </p>
              </div>
            )}
            {requests.map((req) => (
              <RequestCard key={req.id} req={req} onChanged={loadRequests} user={user} />
            ))}
          </div>
        </div>
      </main>

      {/* Desktop Community Info Panel - Hidden on mobile */}
      <div className="hidden lg:block">
        <CommunityInfoPanel />
      </div>

      {/* Mobile Community Info Modal */}
      {showCommunityInfo && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <div className="absolute inset-0 bg-black bg-opacity-50" onClick={() => setShowCommunityInfo(false)}></div>
          <div className="absolute right-0 top-0 bottom-0 w-full max-w-sm bg-white shadow-xl overflow-y-auto">
            <div className="sticky top-0 bg-white border-b px-4 py-3 flex items-center justify-between">
              <h2 className="text-lg font-semibold text-gray-900">Community Info</h2>
              <button
                onClick={() => setShowCommunityInfo(false)}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <XMarkIcon className="h-5 w-5 text-gray-500" />
              </button>
            </div>
            <CommunityInfoPanel />
          </div>
        </div>
      )}

      {/* Availability Settings Modal */}
      {showAvailability && (
        <AvailabilityModal
          onClose={() => setShowAvailability(false)}
          userId={user?.id}
        />
      )}

      {/* Members Availability Modal */}
      {showMembersAvailability && membersAvailability && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[80vh] overflow-hidden flex flex-col">
            <div className="flex items-center justify-between p-4 border-b">
              <h3 className="text-lg font-semibold text-gray-900">
                {membersAvailability.channel_name} - Members' Time Restrictions
              </h3>
              <button
                onClick={() => setShowMembersAvailability(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>
            
            <div className="flex-1 overflow-y-auto p-4 space-y-4">
              {membersAvailability.members.map((member, idx) => (
                <div key={idx} className="border border-gray-200 rounded-lg p-4 bg-gray-50">
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="font-semibold text-gray-900">{member.name}</h4>
                    {!member.has_availability_set && (
                      <span className="text-xs px-2 py-1 bg-green-100 text-green-700 rounded font-medium">
                        No restrictions set
                      </span>
                    )}
                  </div>
                  
                  {member.weekly_blocks.length > 0 && (
                    <div className="mb-3">
                      <div className="text-xs font-medium text-gray-700 mb-2">
                        🚫 Weekly Blocked Times:
                      </div>
                      <div className="space-y-1">
                        {member.weekly_blocks.map((block, i) => (
                          <div key={i} className="text-sm text-gray-600 pl-4 border-l-2 border-red-300">
                            <span className="font-medium">{block.day}</span>: {block.time_range}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  {member.time_off.length > 0 && (
                    <div>
                      <div className="text-xs font-medium text-gray-700 mb-2">
                        📅 Time Off:
                      </div>
                      <div className="space-y-1">
                        {member.time_off.map((off, i) => (
                          <div key={i} className="text-sm text-gray-600 pl-4 border-l-2 border-orange-300">
                            {off.description}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  {!member.has_availability_set && (
                    <div className="text-sm text-gray-600">
                      ✅ This member has not set any time restrictions yet.
                    </div>
                  )}
                </div>
              ))}
            </div>
            
            <div className="p-4 border-t bg-gray-50">
              <button
                onClick={() => setShowMembersAvailability(false)}
                className="w-full px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors font-medium"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const AvailabilityModal = ({ onClose, userId }) => {
  const [availability, setAvailability] = useState({ weekly: [], time_off: [] });
  const [loading, setLoading] = useState(true);
  const [newTimeOff, setNewTimeOff] = useState({ start_iso: '', end_iso: '', reason: '' });

  // Helper to set time to 12:00 AM or 11:59 PM
  const setAllDayTime = (dateStr, isEnd = false) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    if (isEnd) {
      date.setHours(23, 59, 0, 0);
    } else {
      date.setHours(0, 0, 0, 0);
    }
    return date.toISOString().slice(0, 16);
  };

  useEffect(() => {
    loadAvailability();
  }, []);

  const loadAvailability = async () => {
    try {
      const { data } = await api.get('/api/availability/me');
      setAvailability(data.preferences || { weekly: [], time_off: [] });
    } catch (error) {
      console.error('Error loading availability:', error);
    } finally {
      setLoading(false);
    }
  };

  const [weeklyBlockMode, setWeeklyBlockMode] = useState('allday'); // 'allday' or 'custom'
  const [customBlockTime, setCustomBlockTime] = useState({ dow: 1, start_hour: 9, start_min: 0, end_hour: 17, end_min: 0 });

  const toggleDayBlock = (dow) => {
    const existing = availability.weekly.find(r => r.iso_dow === dow);
    if (existing) {
      // Remove
      setAvailability({
        ...availability,
        weekly: availability.weekly.filter(r => r.iso_dow !== dow)
      });
    } else {
      // Add (block entire day by default)
      setAvailability({
        ...availability,
        weekly: [...availability.weekly, { iso_dow: dow, start_min: 0, end_min: 1440 }]
      });
    }
  };

  const addCustomBlock = () => {
    const start_min = customBlockTime.start_hour * 60 + customBlockTime.start_min;
    const end_min = customBlockTime.end_hour * 60 + customBlockTime.end_min;
    
    if (start_min >= end_min) {
      alert('End time must be after start time');
      return;
    }

    setAvailability({
      ...availability,
      weekly: [...availability.weekly, {
        iso_dow: customBlockTime.dow,
        start_min,
        end_min
      }]
    });
  };

  const removeWeeklyBlock = (index) => {
    const newWeekly = [...availability.weekly];
    newWeekly.splice(index, 1);
    setAvailability({ ...availability, weekly: newWeekly });
  };

  const formatTime = (minutes) => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    const period = hours >= 12 ? 'PM' : 'AM';
    const displayHour = hours === 0 ? 12 : hours > 12 ? hours - 12 : hours;
    return `${displayHour}:${mins.toString().padStart(2, '0')} ${period}`;
  };

  const addTimeOff = async () => {
    if (!newTimeOff.start_iso || !newTimeOff.end_iso) {
      alert('Please select start and end dates');
      return;
    }

    try {
      // Ensure times are set to all-day (12:00 AM to 11:59 PM)
      const timeOffData = {
        start_iso: setAllDayTime(newTimeOff.start_iso, false),
        end_iso: setAllDayTime(newTimeOff.end_iso, true),
        reason: newTimeOff.reason
      };
      await api.post('/api/availability/timeoff', timeOffData);
      setNewTimeOff({ start_iso: '', end_iso: '', reason: '' });
      await loadAvailability();
    } catch (error) {
      console.error('Error adding time off:', error);
      alert('Failed to add time off');
    }
  };

  const deleteTimeOff = async (id) => {
    try {
      await api.delete(`/api/availability/timeoff/${id}`);
      await loadAvailability();
    } catch (error) {
      console.error('Error deleting time off:', error);
    }
  };

  const saveWeekly = async () => {
    try {
      await api.post('/api/availability/weekly', { weekly: availability.weekly });
      alert('Availability saved!');
    } catch (error) {
      console.error('Error saving availability:', error);
      alert('Failed to save availability');
    }
  };

  const days = [
    { dow: 1, name: 'Monday' },
    { dow: 2, name: 'Tuesday' },
    { dow: 3, name: 'Wednesday' },
    { dow: 4, name: 'Thursday' },
    { dow: 5, name: 'Friday' },
    { dow: 6, name: 'Saturday' },
    { dow: 7, name: 'Sunday' }
  ];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-50">
      <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b p-4 sm:p-6 flex items-center justify-between">
          <h2 className="text-lg sm:text-xl font-bold text-gray-900">My Availability</h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>

        <div className="p-4 sm:p-6 space-y-6">
          {/* Weekly Blocks */}
          <div>
            <h3 className="font-semibold text-gray-900 mb-3">Weekly Availability Blocks</h3>
            <p className="text-sm text-gray-600 mb-3">
              Block days or specific times when you're unavailable
            </p>

            {/* Mode Toggle */}
            <div className="flex gap-2 mb-3">
              <button
                onClick={() => setWeeklyBlockMode('allday')}
                className={`px-3 py-1.5 rounded-lg text-sm transition-colors ${
                  weeklyBlockMode === 'allday'
                    ? 'bg-blue-100 text-blue-700 font-medium'
                    : 'bg-gray-100 text-gray-600'
                }`}
              >
                Block Full Days
              </button>
              <button
                onClick={() => setWeeklyBlockMode('custom')}
                className={`px-3 py-1.5 rounded-lg text-sm transition-colors ${
                  weeklyBlockMode === 'custom'
                    ? 'bg-blue-100 text-blue-700 font-medium'
                    : 'bg-gray-100 text-gray-600'
                }`}
              >
                Block Time Ranges
              </button>
            </div>

            {weeklyBlockMode === 'allday' ? (
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                {days.map(day => {
                  const isBlocked = availability.weekly.some(r => r.iso_dow === day.dow && r.start_min === 0 && r.end_min === 1440);
                  return (
                    <button
                      key={day.dow}
                      onClick={() => toggleDayBlock(day.dow)}
                      className={`px-3 py-2 rounded-lg border-2 transition-colors text-sm ${
                        isBlocked
                          ? 'border-red-500 bg-red-50 text-red-700'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      {isBlocked && '🚫 '}
                      {day.name}
                    </button>
                  );
                })}
              </div>
            ) : (
              <div className="space-y-3">
                <div className="grid grid-cols-2 gap-2">
                  <select
                    className="border border-gray-300 rounded-lg px-3 py-2 text-sm"
                    value={customBlockTime.dow}
                    onChange={(e) => setCustomBlockTime({ ...customBlockTime, dow: parseInt(e.target.value) })}
                  >
                    {days.map(day => (
                      <option key={day.dow} value={day.dow}>{day.name}</option>
                    ))}
                  </select>
                  <div className="text-xs text-gray-600 flex items-center">From / To</div>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <input
                      type="time"
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                      value={`${customBlockTime.start_hour.toString().padStart(2, '0')}:${customBlockTime.start_min.toString().padStart(2, '0')}`}
                      onChange={(e) => {
                        const [h, m] = e.target.value.split(':');
                        setCustomBlockTime({ ...customBlockTime, start_hour: parseInt(h), start_min: parseInt(m) });
                      }}
                    />
                  </div>
                  <div>
                    <input
                      type="time"
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                      value={`${customBlockTime.end_hour.toString().padStart(2, '0')}:${customBlockTime.end_min.toString().padStart(2, '0')}`}
                      onChange={(e) => {
                        const [h, m] = e.target.value.split(':');
                        setCustomBlockTime({ ...customBlockTime, end_hour: parseInt(h), end_min: parseInt(m) });
                      }}
                    />
                  </div>
                </div>
                <button
                  onClick={addCustomBlock}
                  className="w-full px-3 py-2 border border-blue-600 text-blue-600 rounded-lg hover:bg-blue-50 transition-colors text-sm"
                >
                  + Add Time Block
                </button>
              </div>
            )}

            {/* Show existing blocks */}
            {availability.weekly && availability.weekly.length > 0 && (
              <div className="mt-4 space-y-2">
                <div className="text-xs font-semibold text-gray-500 uppercase">Current Blocks:</div>
                {availability.weekly.map((block, idx) => {
                  const day = days.find(d => d.dow === block.iso_dow);
                  const isAllDay = block.start_min === 0 && block.end_min === 1440;
                  return (
                    <div key={idx} className="flex items-center justify-between p-2 bg-gray-50 rounded text-sm">
                      <span>
                        {day?.name} {isAllDay ? '(All Day)' : `${formatTime(block.start_min)} - ${formatTime(block.end_min)}`}
                      </span>
                      <button
                        onClick={() => removeWeeklyBlock(idx)}
                        className="p-1 text-red-600 hover:bg-red-50 rounded"
                      >
                        <XMarkIcon className="h-4 w-4" />
                      </button>
                    </div>
                  );
                })}
              </div>
            )}

            <button
              onClick={saveWeekly}
              className="mt-3 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm"
            >
              Save Weekly Blocks
            </button>
          </div>

          {/* Time Off */}
          <div>
            <h3 className="font-semibold text-gray-900 mb-3">Time Off</h3>
            <p className="text-sm text-gray-600 mb-3">
              Block specific date ranges
            </p>
            
            <div className="space-y-3 mb-4">
              <p className="text-xs text-gray-600">Time off will block all day (12:00 AM - 11:59 PM)</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                <div>
                  <label className="block text-xs text-gray-600 mb-1">Start Date</label>
                  <input
                    type="date"
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                    value={newTimeOff.start_iso ? newTimeOff.start_iso.split('T')[0] : ''}
                    onChange={(e) => setNewTimeOff({ ...newTimeOff, start_iso: e.target.value ? `${e.target.value}T00:00` : '' })}
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-600 mb-1">End Date</label>
                  <input
                    type="date"
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                    value={newTimeOff.end_iso ? newTimeOff.end_iso.split('T')[0] : ''}
                    onChange={(e) => setNewTimeOff({ ...newTimeOff, end_iso: e.target.value ? `${e.target.value}T23:59` : '' })}
                  />
                </div>
              </div>
              <input
                type="text"
                placeholder="Reason (optional)"
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                value={newTimeOff.reason}
                onChange={(e) => setNewTimeOff({ ...newTimeOff, reason: e.target.value })}
              />
              <button
                onClick={addTimeOff}
                className="w-full sm:w-auto px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm"
              >
                + Add Time Off
              </button>
            </div>

            {/* Time Off List */}
            <div className="space-y-2">
              {availability.time_off && availability.time_off.length > 0 ? (
                availability.time_off.map((timeOff) => (
                  <div key={timeOff.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium text-gray-900">
                        {new Date(timeOff.start_ts).toLocaleDateString()} - {new Date(timeOff.end_ts).toLocaleDateString()}
                      </div>
                      {timeOff.reason && (
                        <div className="text-xs text-gray-600 truncate">{timeOff.reason}</div>
                      )}
                    </div>
                    <button
                      onClick={() => deleteTimeOff(timeOff.id)}
                      className="ml-2 p-1 text-red-600 hover:bg-red-50 rounded"
                    >
                      <XMarkIcon className="h-4 w-4" />
                    </button>
                  </div>
                ))
              ) : (
                <p className="text-sm text-gray-500 text-center py-4">No time off scheduled</p>
              )}
            </div>
          </div>
        </div>

        <div className="sticky bottom-0 bg-gray-50 border-t p-4 flex justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-gray-900 text-white rounded-lg hover:bg-gray-800 transition-colors"
          >
            Done
          </button>
        </div>
      </div>
    </div>
  );
};

const RequestCard = ({ req, onChanged, user }) => {
  const [responding, setResponding] = useState(false);

  const respond = async (slotId, availability) => {
    if (responding) return;
    
    try {
      setResponding(true);
      await api.post(`/api/scheduling/requests/${req.id}/respond`, {
        slot_id: slotId,
        availability: availability
      });
      await onChanged();
    } catch (error) {
      console.error('Error responding:', error);
      alert('Failed to record response');
    } finally {
      setResponding(false);
    }
  };

  const finalize = async () => {
    if (!window.confirm('Finalize this meeting request? The best time slot will be selected.')) {
      return;
    }

    try {
      await api.post(`/api/scheduling/requests/${req.id}/finalize`);
      await onChanged();
      alert('Meeting finalized! Members will receive calendar invites.');
    } catch (error) {
      console.error('Error finalizing:', error);
      alert('Failed to finalize meeting');
    }
  };

  const isCreator = req.created_by === user?.id;
  const isFinalized = !!req.final_slot_id;

  return (
    <div className="bg-white p-6 rounded-xl border shadow-sm">
      <div className="flex items-start justify-between mb-4">
        <div className="flex-1">
          <h3 className="font-semibold text-lg text-gray-900">{req.title}</h3>
          {req.purpose && (
            <p className="text-sm text-gray-600 mt-1">{req.purpose}</p>
          )}
          <div className="flex items-center gap-4 mt-2 text-xs text-gray-500">
            <span>By {req.created_by_name}</span>
            <span className="capitalize">{req.modality.replace('_', ' ')}</span>
            {req.location && <span>📍 {req.location}</span>}
          </div>
        </div>
        {isCreator && !isFinalized && (
          <button
            onClick={finalize}
            className="px-3 py-1.5 bg-green-600 text-white text-sm rounded-lg hover:bg-green-700 transition-colors"
          >
            Finalize
          </button>
        )}
        {isFinalized && (
          <span className="px-3 py-1.5 bg-green-100 text-green-700 text-sm rounded-lg font-medium">
            ✓ Finalized
          </span>
        )}
      </div>

      <div className="space-y-2">
        {req.slots && req.slots.map((slot) => (
          <div
            key={slot.id}
            className={`p-4 rounded-lg border-2 transition-colors ${
              slot.id === req.final_slot_id
                ? 'border-green-500 bg-green-50'
                : 'border-gray-200 bg-gray-50'
            }`}
          >
            <div className="flex items-center justify-between mb-2">
              <div className="font-medium text-gray-900">
                {new Date(slot.start_ts).toLocaleString('en-US', {
                  weekday: 'short',
                  month: 'short',
                  day: 'numeric',
                  hour: 'numeric',
                  minute: '2-digit'
                })}
                {' → '}
                {new Date(slot.end_ts).toLocaleTimeString('en-US', {
                  hour: 'numeric',
                  minute: '2-digit'
                })}
              </div>
              {slot.id === req.final_slot_id && (
                <span className="text-xs font-semibold text-green-700">
                  SELECTED
                </span>
              )}
            </div>

            <div className="flex items-center gap-4 text-sm text-gray-600 mb-3">
              <span className="flex items-center gap-1">
                <CheckIcon className="h-4 w-4 text-green-600" />
                {slot.yes_count || 0} Available
              </span>
              <span className="flex items-center gap-1">
                <ClockIcon className="h-4 w-4 text-yellow-600" />
                {slot.if_needed_count || 0} If Needed
              </span>
              <span className="flex items-center gap-1">
                <XMarkIcon className="h-4 w-4 text-red-600" />
                {slot.no_count || 0} Unavailable
              </span>
            </div>

            {!isFinalized && (
              <div className="flex gap-2">
                <button
                  onClick={() => respond(slot.id, 'yes')}
                  disabled={responding}
                  className="flex-1 px-3 py-2 border border-green-300 text-green-700 rounded-lg hover:bg-green-50 transition-colors text-sm font-medium disabled:opacity-50"
                >
                  Available
                </button>
                <button
                  onClick={() => respond(slot.id, 'if_needed')}
                  disabled={responding}
                  className="flex-1 px-3 py-2 border border-yellow-300 text-yellow-700 rounded-lg hover:bg-yellow-50 transition-colors text-sm font-medium disabled:opacity-50"
                >
                  If Needed
                </button>
                <button
                  onClick={() => respond(slot.id, 'no')}
                  disabled={responding}
                  className="flex-1 px-3 py-2 border border-red-300 text-red-700 rounded-lg hover:bg-red-50 transition-colors text-sm font-medium disabled:opacity-50"
                >
                  Unavailable
                </button>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default SchedulerSystem;
